'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ReactNode, useState, useEffect } from 'react';
import FadeIn from '../ui/FadeIn';
import { ChevronLeft } from 'lucide-react';

interface TOCItem {
    id: string;
    text: string;
}

interface LegalLayoutProps {
    title: string;
    subtitle: string;
    toc: TOCItem[];
    effectiveDate?: string;
    lastUpdated?: string;
    children: ReactNode;
}

export default function LegalLayout({
    title,
    subtitle,
    toc,
    effectiveDate,
    lastUpdated,
    children
}: LegalLayoutProps) {
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const updateScroll = () => {
            const currentScroll = window.scrollY;
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollHeight) {
                setScrollProgress(Number((currentScroll / scrollHeight).toFixed(2)) * 100);
            }
        };

        window.addEventListener('scroll', updateScroll);
        return () => window.removeEventListener('scroll', updateScroll);
    }, []);

    return (
        <main className="min-h-screen bg-[var(--color-md-surface-variant)] pt-32 pb-32 relative overflow-hidden noise-bg">
            {/* Reading Progress Bar */}
            <div
                className="fixed top-0 left-0 h-1 bg-[var(--color-md-primary)] z-[100] transition-all duration-150 ease-out"
                style={{ width: `${scrollProgress}%` }}
            />

            {/* Cinematic Ambient Glows */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[var(--color-md-primary)]/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[var(--color-md-primary)]/5 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-6 max-w-[1240px] relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-16 xl:gap-24">

                    {/* Navigation Sidebar */}
                    <aside className="hidden lg:block sticky top-32 h-fit">
                        <FadeIn delay={0.1}>
                            <Link href="/" className="group flex items-center gap-3 mb-12 text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)] transition-all duration-300">
                                <div className="w-10 h-10 rounded-2xl bg-white border border-[var(--color-md-outline)] flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform group-hover:border-[var(--color-md-primary)]/30">
                                    <ChevronLeft className="w-4 h-4" strokeWidth={3} />
                                </div>
                                <span className="text-sm font-bold">Back</span>
                            </Link>

                            <div className="mb-10">
                                <div className="text-xs font-bold text-[var(--color-md-primary)] mb-6 pl-1 px-3">
                                    Contents
                                </div>
                                <nav className="space-y-1">
                                    {toc.map((item, idx) => (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="group flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-white hover:shadow-sm border border-transparent hover:border-[var(--color-md-outline)] transition-all duration-200"
                                        >
                                            <span className="text-[11px] font-black text-[var(--color-md-on-surface-variant)] group-hover:text-[var(--color-md-primary)] transition-colors w-4">
                                                {String(idx + 1).padStart(2, '0')}
                                            </span>
                                            <span className="text-[13px] font-bold text-[var(--color-md-on-surface-variant)] group-hover:text-[var(--color-md-on-surface)] transition-colors">
                                                {item.text}
                                            </span>
                                        </a>
                                    ))}
                                </nav>
                            </div>

                        </FadeIn>
                    </aside>

                    {/* Document Area */}
                    <article className="max-w-[840px]">

                        {/* Executive Header */}
                        <FadeIn delay={0.2} className="mb-16">
                            <div className="flex items-center gap-4 mb-10">
                                <div className="w-16 h-16 rounded-[24px] bg-white shadow-cinematic-2 border border-[var(--color-md-outline)] p-3.5 flex items-center justify-center">
                                    <Image src="/icon.png" width={40} height={40} alt="App Icon" />
                                </div>
                                <div className="h-10 w-px bg-[var(--color-md-outline)]" />
                                <div className="space-y-1">
                                    <div className="text-xs font-bold text-[var(--color-md-primary)]">
                                        Trust Protocol
                                    </div>
                                    <div className="text-xs font-bold text-[var(--color-md-on-surface-variant)]">
                                        Legally Binding Document
                                    </div>
                                </div>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-8 leading-tight">
                                {title}
                            </h1>
                            <p className="text-xl md:text-2xl text-[var(--color-md-on-surface-variant)] leading-relaxed font-bold max-w-2xl opacity-80 decoration-[var(--color-md-primary)]/20 underline-offset-8">
                                {subtitle}
                            </p>

                            {(effectiveDate || lastUpdated) && (
                                <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-8 border-t border-[var(--color-md-outline)]/20 pt-6">
                                    {effectiveDate && (
                                        <div>
                                            <div className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-primary)] mb-1">Effective Date</div>
                                            <div className="text-sm font-medium text-[var(--color-md-on-surface-variant)]">{effectiveDate}</div>
                                        </div>
                                    )}
                                    {lastUpdated && (
                                        <div>
                                            <div className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-primary)] mb-1">Last Updated</div>
                                            <div className="text-sm font-medium text-[var(--color-md-on-surface-variant)]">{lastUpdated}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </FadeIn>

                        {/* The "Sheet" - Simplified for Professional Clarity */}
                        <FadeIn delay={0.3}>
                            <div className="relative">
                                {/* Removed 'cinematic' containers/shadows for a pure reading experience */}

                                <div className="py-8">
                                    <div className="prose max-w-none
                                        prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-[var(--color-md-on-surface)]
                                        
                                        prose-h1:text-[length:var(--font-display-small)] prose-h1:leading-[var(--line-display-small)] prose-h1:tracking-[var(--tracking-display-small)]
                                        
                                        prose-h2:text-[32px] prose-h2:leading-tight prose-h2:mt-32 prose-h2:mb-8 prose-h2:border-b prose-h2:border-[var(--color-md-outline)]/20 prose-h2:pb-4
                                        prose-h3:text-[24px] prose-h3:leading-snug prose-h3:mt-16 prose-h3:mb-6 prose-h3:text-[var(--color-md-on-surface)]
                                        
                                        prose-p:text-[18px] prose-p:leading-[1.8] prose-p:tracking-normal prose-p:max-w-[68ch]
                                        prose-p:text-[var(--color-md-on-surface-variant)] prose-p:font-normal prose-p:mb-8
                                        
                                        prose-a:text-[var(--color-md-primary)] prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                                        prose-strong:text-[var(--color-md-on-surface)] prose-strong:font-bold
                                        
                                        prose-li:text-[18px] prose-li:leading-[1.8] prose-li:tracking-normal prose-li:max-w-[68ch]
                                        prose-li:text-[var(--color-md-on-surface-variant)] prose-li:font-normal
                                        prose-ul:my-8 prose-li:my-3 prose-ul:pl-0 prose-ul:list-none

                                        prose-code:text-[15px] prose-code:font-medium prose-code:text-[var(--color-md-primary)] prose-code:bg-[var(--color-md-primary-container)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:before:content-none prose-code:after:content-none
                                    ">
                                        {children}
                                    </div>
                                </div>

                                {/* Minimal Footer */}
                                <div className="mt-24 pt-12 border-t border-[var(--color-md-outline)]/30 flex flex-col sm:flex-row items-center justify-between gap-6 opacity-60">
                                    <div className="flex items-center gap-3">
                                        <div className="text-[12px] font-medium text-[var(--color-md-on-surface-variant)]">
                                            © 2026 WaVault Systems Inc.
                                        </div>
                                    </div>
                                    <div className="text-[11px] font-mono text-[var(--color-md-on-surface-variant)]">
                                        REF: SEC_DOC_102
                                    </div>
                                </div>
                            </div>
                        </FadeIn>


                    </article>

                </div>
            </div>
        </main>
    );
}
